/**
 * ProjectContext - DB-backed project state management
 *
 * Provides activeProject, projects list, and mutation methods.
 * Replaces workspace-state based project storage.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useApi } from './ApiContext';
import { useAuth } from './AuthContext';

export interface Project {
  id: string;
  userId: string;
  name: string;
  description?: string;
  sessionId?: string;
  createdAt: string;
  updatedAt: string;
}

interface ProjectContextValue {
  projects: Project[];
  activeProject: Project | null;
  activeProjectId: string | null;
  setActiveProject: (id: string | null) => void;
  refreshProjects: () => Promise<void>;
  createProject: (name: string, description?: string, sessionId?: string) => Promise<Project>;
  updateProject: (id: string, update: { name?: string; description?: string }) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  // Project-buffer associations
  projectBufferIds: string[];
  addBufferToProject: (bufferId: string) => Promise<void>;
  removeBufferFromProject: (bufferId: string) => Promise<void>;
  refreshProjectBuffers: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const api = useApi();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [projectBufferIds, setProjectBufferIds] = useState<string[]>([]);

  // ELECTRON: Skip cloud API calls in local-only mode
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;

  const activeProject = projects.find((p) => p.id === activeProjectId) ?? null;

  const refreshProjects = useCallback(async () => {
    try {
      if (isElectron && window.electronHarvest) {
        // Local mode: get profile ID as user ID
        const profile = window.electronLocalAuth
          ? await window.electronLocalAuth.getActiveProfile()
          : null;
        const userId = profile?.id ?? 'local';
        const list = await window.electronHarvest.listProjects(userId);
        setProjects(list.map((p: any) => ({
          id: p.id,
          userId: p.userId,
          name: p.name,
          description: p.description ?? undefined,
          createdAt: String(p.createdAt),
          updatedAt: String(p.updatedAt),
        })));
      } else {
        const { projects: list } = await api.projects.list();
        setProjects(list);
      }
    } catch {
      // Silently fail on load
    }
  }, [api, isElectron]);

  // Load projects on mount
  useEffect(() => {
    if (isElectron) {
      // Electron: load from local IPC + restore activeProjectId from store
      refreshProjects();
      window.electronAPI?.store.get('activeProjectId').then((value) => {
        if (typeof value === 'string') setActiveProjectId(value);
      }).catch(() => {});
      return;
    }
    if (!isAuthenticated || authLoading) return;
    refreshProjects();
    // Restore active project ID from workspace state
    api.workspaceState.get('active_project_id').then(({ value }) => {
      if (typeof value === 'string') setActiveProjectId(value);
    }).catch(() => {});
  }, [api, refreshProjects, isAuthenticated, authLoading, isElectron]);

  const setActiveProject = useCallback((id: string | null) => {
    setActiveProjectId(id);
    if (isElectron) {
      // Persist to electron-store
      window.electronAPI?.store.set('activeProjectId', id).catch(() => {});
    } else {
      api.workspaceState.set('active_project_id', id).catch(() => {});
    }
  }, [api, isElectron]);

  const createProject = useCallback(async (name: string, description?: string, sessionId?: string) => {
    if (isElectron && window.electronHarvest) {
      const profile = window.electronLocalAuth
        ? await window.electronLocalAuth.getActiveProfile()
        : null;
      const userId = profile?.id ?? 'local';
      const p = await window.electronHarvest.createProject(userId, name, description);
      const project: Project = {
        id: p.id,
        userId: p.userId,
        name: p.name,
        description: p.description ?? undefined,
        createdAt: String(p.createdAt),
        updatedAt: String(p.updatedAt),
      };
      setProjects((prev) => [project, ...prev]);
      setActiveProject(project.id);
      return project;
    }
    const { project } = await api.projects.create({ name, description, sessionId });
    setProjects((prev) => [project, ...prev]);
    setActiveProject(project.id);
    return project;
  }, [api, setActiveProject, isElectron]);

  const updateProject = useCallback(async (id: string, update: { name?: string; description?: string }) => {
    if (isElectron && window.electronHarvest) {
      await window.electronHarvest.updateProject(id, update);
      await refreshProjects();
      return;
    }
    const { project } = await api.projects.update(id, update);
    setProjects((prev) => prev.map((p) => (p.id === id ? project : p)));
  }, [api, isElectron, refreshProjects]);

  const deleteProject = useCallback(async (id: string) => {
    if (isElectron && window.electronHarvest) {
      await window.electronHarvest.deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (activeProjectId === id) {
        setActiveProject(null);
        setProjectBufferIds([]);
      }
      return;
    }
    await api.projects.delete(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
    if (activeProjectId === id) {
      setActiveProject(null);
      setProjectBufferIds([]);
    }
  }, [api, activeProjectId, setActiveProject, isElectron]);

  // ─────────────────────────────────────────────────────────────────────────
  // PROJECT-BUFFER ASSOCIATIONS
  // ─────────────────────────────────────────────────────────────────────────

  const refreshProjectBuffers = useCallback(async () => {
    if (!activeProjectId) {
      setProjectBufferIds([]);
      return;
    }
    try {
      if (isElectron && window.electronHarvest?.listProjectBuffers) {
        const ids = await window.electronHarvest.listProjectBuffers(activeProjectId);
        setProjectBufferIds(ids);
      } else {
        // Cloud: not yet wired
        setProjectBufferIds([]);
      }
    } catch {
      setProjectBufferIds([]);
    }
  }, [activeProjectId, isElectron]);

  // Refresh buffer list when active project changes
  useEffect(() => {
    refreshProjectBuffers();
  }, [refreshProjectBuffers]);

  const addBufferToProject = useCallback(async (bufferId: string) => {
    if (!activeProjectId) return;
    if (isElectron && window.electronHarvest?.addBufferToProject) {
      await window.electronHarvest.addBufferToProject(activeProjectId, bufferId);
      setProjectBufferIds((prev) => prev.includes(bufferId) ? prev : [bufferId, ...prev]);
    }
  }, [activeProjectId, isElectron]);

  const removeBufferFromProject = useCallback(async (bufferId: string) => {
    if (!activeProjectId) return;
    if (isElectron && window.electronHarvest?.removeBufferFromProject) {
      await window.electronHarvest.removeBufferFromProject(activeProjectId, bufferId);
      setProjectBufferIds((prev) => prev.filter((id) => id !== bufferId));
    }
  }, [activeProjectId, isElectron]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo<ProjectContextValue>(() => ({
    projects,
    activeProject,
    activeProjectId,
    setActiveProject,
    refreshProjects,
    createProject,
    updateProject,
    deleteProject,
    projectBufferIds,
    addBufferToProject,
    removeBufferFromProject,
    refreshProjectBuffers,
  }), [
    projects,
    activeProject,
    activeProjectId,
    setActiveProject,
    refreshProjects,
    createProject,
    updateProject,
    deleteProject,
    projectBufferIds,
    addBufferToProject,
    removeBufferFromProject,
    refreshProjectBuffers,
  ]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProject must be used within ProjectProvider');
  return ctx;
}
